"use client"

import { useEffect } from "react"

/**
 * Keyboard + visual-viewport insets. One mechanism, every platform.
 *
 * This used to be browser-only, with NativeShell publishing `--keyboard-h`
 * inside the app from Capacitor's keyboardWillShow. That double-counted the
 * keyboard: the Keyboard plugin is configured to resize the web view (native
 * on most routes, body on the composer), so the viewport has ALREADY shrunk
 * by the keyboard height by the time the event fires. A sheet anchored to
 * `bottom: var(--keyboard-h)` was lifted a second keyboard above the bottom
 * edge, which pushed its top off the screen — the Contact Shop sheet showed
 * its heading with the textarea and the send button out of reach.
 *
 * Measuring the visual viewport cannot double-count, because it reports what
 * is actually left. When the web view resizes, the inset comes out at 0 and
 * `bottom-0` is already right. When it does not — mobile Safari, or a resize
 * mode of none — it reports the real covered height. Same code either way.
 *
 * What goes wrong without this, on iOS Safari: opening the keyboard does not
 * shrink the layout viewport. It shrinks the VISUAL viewport and scrolls it,
 * while `position: fixed` stays pinned to the layout viewport. So the sign-up
 * sheet's lower half (the fields you are typing into) ends up behind the
 * keyboard, and the overlay no longer reaches the top of what you can see —
 * which is how the page behind showed through above the sheet.
 *
 * The three vars are all read off `window.visualViewport`:
 *   --keyboard-h  how much of the layout viewport's bottom is covered
 *                 (keyboard, accessory bar), so bottom-anchored UI can sit on
 *                 top of it.
 *   --vv-top      how far the visual viewport has scrolled down inside the
 *                 layout viewport, so a full-bleed overlay can start there.
 *   --vv-height   the height actually visible.
 *
 * Mounted once in the root layout, beside NativeShell. Inert in the native
 * shell (NativeShell owns --keyboard-h there) and on any browser without the
 * VisualViewport API, where every consumer falls back to its 0px default.
 */
export function ViewportInsets() {
  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return

    const root = document.documentElement
    let frame = 0

    const apply = () => {
      frame = 0
      // Rounded down: a fractional pixel of inset is not worth a repaint, and
      // iOS reports sub-pixel heights while the keyboard animates.
      const bottomInset = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop))
      root.style.setProperty("--keyboard-h", `${bottomInset}px`)
      root.style.setProperty("--vv-top", `${Math.max(0, Math.round(vv.offsetTop))}px`)
      root.style.setProperty("--vv-height", `${Math.round(vv.height)}px`)
    }

    // Coalesce to one write per frame: iOS fires resize and scroll together,
    // many times over, for the length of the keyboard animation.
    const schedule = () => {
      if (frame) return
      frame = requestAnimationFrame(apply)
    }

    apply()
    vv.addEventListener("resize", schedule)
    vv.addEventListener("scroll", schedule)
    return () => {
      if (frame) cancelAnimationFrame(frame)
      vv.removeEventListener("resize", schedule)
      vv.removeEventListener("scroll", schedule)
      root.style.removeProperty("--keyboard-h")
      root.style.removeProperty("--vv-top")
      root.style.removeProperty("--vv-height")
    }
  }, [])

  return null
}
